import { Typography, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ProductBasicInformation from './ProductBasicInformation'
import { useAppDispatch } from '../../hooks/reduxHooks'
import { RouteConstants } from '../../navigation/navigation-types'
import { displayMessage } from '../../services/redux/snackbar/snackbar-actions'
import { ProductManager } from '../../services/api/ProductManager'
import { ProductWithStatisticsDto } from '../../services/api/sharedTypes/Product'
import ScreenLoadingPlaceholder from '../../components/ScreenLoadingPlaceholder'
import { EventOfProductDto } from '../../services/api/sharedTypes/Event'
import ProductEventTable from '../../components/product/ProductEventTable'
import { setSaveState } from '../../services/redux/savestate/savestate-actions'
import CustomContainer from '../../components/common/CustomContainer'
import BottomBar from '../../components/common/BottomBar'

const ProductDetail: React.FC = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const theme = useTheme()
    const dispatch = useAppDispatch()

    const [loadingData, setLoadingData] = useState<boolean>(false)
    const [product, setProduct] = useState<ProductWithStatisticsDto>()
    const [oldProduct, setOldProduct] = useState<ProductWithStatisticsDto>()
    const [events, setEvents] = useState<EventOfProductDto[]>()

    if (!id)
        return (
            <Typography variant='h4' color='error' textAlign='center' marginY={theme.spacing(3)}>
                Tenta služba neexistuje
            </Typography>
        )

    const canGoBack =
        product?.name === oldProduct?.name &&
        product?.canBeAssigned === oldProduct?.canBeAssigned &&
        product?.note === oldProduct?.note &&
        product?.productType === oldProduct?.productType &&
        product?.yearOfPurchased === oldProduct?.yearOfPurchased &&
        product?.totalQuantity === oldProduct?.totalQuantity

    useEffect(() => {
        dispatch(setSaveState(canGoBack))
    }, [canGoBack, dispatch])

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true)

            try {
                const productManager = ProductManager.getManager()
                const productRes = await productManager.getProductAsync(id)
                const eventsRes = await productManager.getEventsOfProductAsync(id)

                if (productRes) {
                    setProduct(productRes)
                    setOldProduct(productRes)
                }
                if (eventsRes) {
                    setEvents(eventsRes.events)
                }
            } catch (error) {
                setLoadingData(false)
                dispatch(displayMessage({ message: error, type: 'error' }))
                return navigate(RouteConstants.workersAndCarriers)
            }

            setLoadingData(false)
        }

        fetchData()
    }, [setLoadingData])

    const saveProduct = async () => {
        if (!product) return

        setLoadingData(true)
        const productManager = ProductManager.getManager()

        try {
            const productRes = await productManager.updateProductAsync(id, product)
            if (productRes) {
                dispatch(displayMessage({ message: `Služba byla uložena`, type: 'success' }))
            }
        } catch (error) {
            setLoadingData(false)
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
        setLoadingData(false)
    }

    const deleteProduct = async () => {
        setLoadingData(true)

        const productManager = ProductManager.getManager()
        try {
            await productManager.deleteProductAsync(id)
        } catch (error) {
            setLoadingData(false)
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
        setLoadingData(false)

        navigate(RouteConstants.product)
        dispatch(displayMessage({ message: `Služba byla smazána`, type: 'success' }))
        dispatch(setSaveState(true))
    }

    if (loadingData) return <ScreenLoadingPlaceholder />

    if (!product)
        return (
            <Typography variant='h4' color='error' textAlign='center' marginY={theme.spacing(3)}>
                Tenta služba neexistuje
            </Typography>
        )

    return (
        <CustomContainer>
            <ProductBasicInformation product={product} setProduct={setProduct} />
            {events && (
                <ProductEventTable
                    events={events}
                    costs={product.statistics.costs}
                    operatingTime={product.statistics.operatingTime}
                    profit={product.statistics.profit}
                    revenue={product.statistics.revenue}
                />
            )}
            <BottomBar onDelete={deleteProduct} onSave={saveProduct} defaultRoute={RouteConstants.product} disabled={loadingData} deletedEntityName={product.name} />
        </CustomContainer>
    )
}

export default ProductDetail
